/* Shared UI primitives for the OwnPath v20 state-first explorer. */
import React from 'react';
import { ExternalLink } from 'lucide-react';

export const CHECK_SOURCE = 'Check official source';

export const STATUS = {
  green: { dot: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-900 border-emerald-300', bar: 'from-emerald-400 to-emerald-600' },
  blue:  { dot: 'bg-blue-500',    chip: 'bg-blue-100 text-blue-900 border-blue-300',          bar: 'from-blue-400 to-indigo-500' },
  amber: { dot: 'bg-amber-500',   chip: 'bg-amber-100 text-amber-900 border-amber-300',       bar: 'from-amber-400 to-orange-500' },
  grey:  { dot: 'bg-gray-400',    chip: 'bg-gray-100 text-gray-800 border-gray-300',          bar: 'from-gray-300 to-gray-400' },
  red:   { dot: 'bg-rose-500',    chip: 'bg-rose-100 text-rose-900 border-rose-300',          bar: 'from-rose-400 to-rose-600' },
};

export const orSource = (v) => (v && String(v).trim() ? v : CHECK_SOURCE);

export function SectionHead({ n, icon: Icon, title, sub, testid }) {
  return (
    <div className="flex items-start gap-3 mb-4" data-testid={testid}>
      <div className="w-8 h-8 rounded-full bg-rose-600 text-white text-sm font-extrabold flex items-center justify-center shrink-0 mt-0.5">{n}</div>
      <div className="min-w-0">
        <h3 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
          {Icon && <Icon size={17} className="text-rose-700 shrink-0" />}{title}
        </h3>
        {sub && <p className="text-xs text-gray-600 mt-0.5 leading-snug">{sub}</p>}
      </div>
    </div>
  );
}

export function Badge({ tone = 'grey', children, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS[tone]?.chip || STATUS.grey.chip}`} data-testid={testid}>
      {children}
    </span>
  );
}

export function StatusChip({ colour, label, testid }) {
  const s = STATUS[colour] || STATUS.grey;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${s.chip}`} data-testid={testid}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />{label}
    </span>
  );
}

export function SourceLink({ href, label = CHECK_SOURCE, testid, className = '' }) {
  if (!href) {
    return <span className="text-xs font-semibold text-gray-500 italic" data-testid={testid}>{CHECK_SOURCE} — no URL supplied</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900 hover:bg-black text-white text-xs font-bold transition-colors ${className}`}
      data-testid={testid}>
      {label} <ExternalLink size={11} />
    </a>
  );
}

export function LazyImg({ image, className = '', testid }) {
  if (!image?.path) {
    return <div className={`bg-gradient-to-br from-rose-50 to-amber-50 flex items-center justify-center text-[10px] text-gray-400 ${className}`} data-testid={testid}>No image supplied</div>;
  }
  return <img src={image.path} alt={image.alt || ''} loading="lazy" decoding="async" className={`object-cover ${className}`} data-testid={testid} />;
}

export function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-1">{label}</p>
      <div className="text-sm text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
}

export function Chips({ items, tone = 'bg-gray-50 text-gray-800 border-gray-200' }) {
  if (!items?.length) return <span className="text-xs text-gray-500 italic">{CHECK_SOURCE}</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((x, i) => <span key={i} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${tone}`}>{x}</span>)}
    </div>
  );
}

export const StatusLegend = () => (
  <div className="flex flex-wrap gap-2 text-[11px]" data-testid="v20-status-legend">
    {[['green', 'Open window verified'], ['blue', 'Active source — check intake'], ['amber', 'Cyclical / re-verify'], ['grey', 'Support institution, not funding']].map(([c, l]) => (
      <span key={c} className="inline-flex items-center gap-1.5 text-gray-700"><span className={`w-2.5 h-2.5 rounded-full ${STATUS[c].dot}`} />{l}</span>
    ))}
  </div>
);
